import Helper from '../../../../Common/Helper';
import { appContext } from '../../../../AppContext';
import { IbssComponent } from '../../../../Components/Core/BaseComponent/IbssComponent';
import { JSX } from 'react';
import { SerendieSymbol } from '@serendie/symbols';
import AddSolutionPackageDialog from './AddSolutionPackageDialog';
import { SolutionPackage } from './DataModels';
import { Guid, TypeHelper } from '@iconics/core';
import { Button, ButtonBar, Card, DataGrid, DeleteButton, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, OnSectionUpdateHandler, RightButtons } from '@iconics/components';

class EditSolutionPackages extends IbssComponent<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }

    private userCanUpdate: boolean;
    private userCanDelete: boolean;
    private userCanEnable: boolean;
    private userCanDisable: boolean;

    constructor(props: IProps)
    {
        super(props);
        this.state =
        {
            grid: { rows: [], checkedIds: [] },
            gridRefreshToken: Guid.new().toString(),
            showAddPackageDialog: false,
        };

        this.userCanUpdate = this.local.hasRight('DATAMODEL.Packages.Update');
        this.userCanDelete = this.local.hasRight('DATAMODEL.Packages.Delete');
        this.userCanEnable = this.local.hasRight('API.Packages.Enable');
        this.userCanDisable = this.local.hasRight('API.Packages.Disable');
    }

    private refreshGrid(): void
    {
        this.solutionPackages = undefined;
        this.setState({ gridRefreshToken: Guid.new().toString() });
    }

    private async handleDisable(): Promise<void> 
    {
        await this.loading(async () =>
        {
            const packageId = this.checkedIds[0];
            if (!packageId)
            {
                return;
            }
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.byId.disablepackage.post(
                {
                    tenancy: appContext().tenantId,
                    id: this.props.solutionId,
                    body: { Package_ID: packageId }
                });
            this.refreshGrid();
        });
    }

    private async handleEnable(): Promise<void> 
    {
        await this.loading(async () =>
        {
            const packageId = this.checkedIds[0];
            if (!packageId)
            {
                return;
            }
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.byId.enablepackage.post(
                {
                    tenancy: appContext().tenantId,
                    id: this.props.solutionId,
                    body: { Package_ID: packageId }
                });
            this.refreshGrid();
        });
    }

    private async handleDelete(): Promise<void> 
    {
        await this.loading(async () =>
        {
            let index = 0;
            while (index < this.checkedIds.length)
            {
                const packageId = this.checkedIds[index];
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.byId.removepackage.post(
                    {
                        tenancy: appContext().tenantId,
                        id: this.props.solutionId,
                        body: { Package_ID: packageId }
                    });
                index = index + 1;
            }
            this.refreshGrid();
        });
    }

    private handlePackageAdded(): void
    {
        this.setState({ showAddPackageDialog: false });
        this.refreshGrid();
    }

    private solutionPackages?: IPackage[];
    private async handleRowsRequest(request: IGridRowsRequest<IPackage>): Promise<IGridRowsResponse<IPackage>>
    {
        if (!this.solutionPackages)
        {
            const solutionPackagesData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.byId.packages.get<SolutionPackage[]>({
                id: this.props.solutionId,
                tenancy: appContext().tenantId,
            });

            this.solutionPackages = solutionPackagesData.map(i => ({
                id: i.Id,
                eTag: i['@odata.etag'],
                isEnabled: TypeHelper.toBoolean(i.IsEnabled) ?? false,
                name: i.Name,
                description: i.Description,
            }));
        }

        const sortedRows = DataGrid.sort(this.solutionPackages, request.sorter);
        return { rows: sortedRows, nextSkipToken: '' };
    }

    private get checkedIds(): string[]
    {
        return this.state.grid.checkedIds ?? [];
    }

    public render(): JSX.Element
    {
        return (
            <Card
                title={this.labels.Platform_Solution_PackageListTitle}
                subTitle={this.labels.Platform_Solution_PackageListSubTitle}
                onSectionUpdate={i => this.props.onSectionUpdate(i)}
            >
                <GridContainer>
                    <GridItem>
                        <ButtonBar>
                            <RightButtons>
                                <DeleteButton
                                    small={true}
                                    onDelete={() => this.handleDelete()}
                                    dialogMessage={this.labels.Platform_SolutionPackage_ConfirmDeleteMsg}
                                    disabled={!this.userCanDelete || this.checkedIds.length !== 1}
                                />
                                <Button
                                    size='small'
                                    styleType='ghost'
                                    leftIcon={<SerendieSymbol name='close' />}
                                    onClick={() => this.handleDisable()}
                                    disabled={!this.userCanDisable || this.checkedIds.length !== 1}
                                >
                                    {this.labels.Platform_CommonAction_Disable}
                                </Button>
                                <Button
                                    size='small'
                                    styleType='ghost'
                                    leftIcon={<SerendieSymbol name='check' />}
                                    onClick={() => this.handleEnable()}
                                    disabled={!this.userCanEnable || this.checkedIds.length !== 1}
                                >
                                    {this.labels.Platform_CommonAction_Enable}
                                </Button>
                                <Button
                                    size='small'
                                    styleType='ghost'
                                    leftIcon={<SerendieSymbol name='pencil' />}
                                    onClick={() => this.props.handleEditPackage(this.checkedIds[0])}
                                    disabled={!this.userCanUpdate || this.checkedIds.length !== 1}
                                >
                                    {this.labels.Platform_CommonAction_Edit}
                                </Button>
                                <Button
                                    size='small'
                                    styleType='ghost'
                                    leftIcon={<SerendieSymbol name='plus' />}
                                    onClick={() => this.setState({ showAddPackageDialog: true })}
                                    disabled={!this.userCanUpdate || this.checkedIds.length !== 0}
                                >
                                    {this.labels.Platform_CommonAction_Add}
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </GridItem>
                    <GridItem>
                        <DataGrid
                            columns={[
                                {
                                    columnId: Helper.nameOf<IPackage>("name"),
                                    title: this.labels.Package_Name,
                                    getCellValue: i => i.name,
                                    columnWidth: 40,
                                    sortable: true,
                                },
                                {
                                    columnId: Helper.nameOf<IPackage>("description"),
                                    title: this.labels.Package_Description,
                                    getCellValue: i => i.description,
                                    columnWidth: 40,
                                    sortable: true,
                                },
                                {
                                    columnId: Helper.nameOf<IPackage>("isEnabled"),
                                    title: this.labels.Package_IsEnabled,
                                    getCellValue: i =>
                                        <SerendieSymbol
                                            name={i.isEnabled ? 'check-circle' : 'close-circle'}
                                            style={{ marginLeft: '32px' }}
                                        />,
                                    columnWidth: 20,
                                    sortable: true,
                                },
                            ]}
                            data={this.state.grid}
                            onChange={i => this.setState({ grid: i })}
                            rowId={i => i.id}
                            onRowsRequest={i => this.handleRowsRequest(i)}
                            onCellClicked={i => this.props.handleEditPackage(i.id)}
                            showCheckBoxes={true}
                            allowOnlyOneCheckBox={true}
                            refreshToken={this.state.gridRefreshToken}
                        />
                    </GridItem>
                </GridContainer>
                <AddSolutionPackageDialog
                    open={this.state.showAddPackageDialog}
                    onClose={() => this.setState({ showAddPackageDialog: false })}
                    onSave={() => this.handlePackageAdded()}
                    solutionId={this.props.solutionId}
                />
            </Card>
        )
    }
}

export default EditSolutionPackages;

export interface IProps
{
    solutionId: string,
    onSectionUpdate: OnSectionUpdateHandler;
    handleEditPackage: (id: string) => void;
}

export interface IState
{
    grid: IGridData<IPackage>;
    gridRefreshToken: string;
    showAddPackageDialog: boolean,
}

export interface IPackage
{
    id: string;
    name: string;
    description: string;
    isEnabled: boolean;
    eTag: string;
}
